import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Autocomplete } from '@/modules/common';
import { TextStyleItem } from '@/modules/text-styles/types';

import { TextStyleTabItem } from '..';
import { addEventTextStyleSchema } from '../schema';

interface IProps {
  isOpen: boolean;
  textStylesData: TextStyleItem[];
  selectedTextStyle: TextStyleTabItem | null;
  onOpenChange: (open: boolean) => void;
  onAdd: (textStyle: TextStyleTabItem) => void;
  onEdit: (textStyle: TextStyleTabItem) => void;
}

export type AddEventTextStyleValues = z.infer<typeof addEventTextStyleSchema>;

const defaultTextStyle: TextStyleTabItem = {
  textStyleId: '',
};

export function AddEditTextStyleDialog({
  isOpen,
  onOpenChange,
  onEdit,
  onAdd,
  textStylesData,
  selectedTextStyle,
}: IProps) {
  const isEdit = selectedTextStyle !== null;

  const form = useForm<AddEventTextStyleValues>({
    resolver: zodResolver(addEventTextStyleSchema),
    defaultValues: defaultTextStyle,
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty },
  } = form;
  const textStyleId = watch('textStyleId');

  const textStylesOptions = textStylesData.map((ts) => ({ value: ts.name, id: ts.id }));

  const selectedTextStyleOption = textStylesData.find((ts) => ts.id === textStyleId);

  const handleClose = (open: boolean) => {
    onOpenChange(open);
    form.reset(defaultTextStyle);
  };

  const handleFormSubmit = (values: AddEventTextStyleValues) => {
    const item: TextStyleTabItem = {
      textStyleId: values.textStyleId,
      name: selectedTextStyleOption?.name ?? '',
      content: selectedTextStyleOption?.content ?? '',
    };
    if (isEdit) {
      onEdit(item);
    } else {
      onAdd(item);
    }
    handleClose(false);
  };

  useEffect(() => {
    if (isEdit) {
      form.reset(selectedTextStyle);
    }
  }, [isEdit]);

  return (
    <Dialog onOpenChange={handleClose} open={isOpen} modal={false}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Text Style</DialogTitle>
          <DialogDescription>Description</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleSubmit(handleFormSubmit)(event);
            }}
          >
            <div className="flex flex-col gap-4">
              <Autocomplete
                label="Text Style"
                name="textStyleId"
                control={control}
                searchable
                placeholder="Select name"
                options={textStylesOptions}
              />

              <FormItem>
                <FormLabel>Content</FormLabel>
                <FormControl>
                  <Textarea
                    value={selectedTextStyleOption?.content ?? ''}
                    placeholder="Content will appear here"
                    className="min-h-[80px]"
                    disabled
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            </div>
            <DialogFooter className="mt-6 gap-4">
              <DialogClose asChild>
                <Button variant="outline" data-testid="cancel-button">
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="submit"
                data-testid="save-button"
                disabled={!isDirty}
              >
                {isEdit ? 'Save' : 'Add Text Style'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
